import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { CustomTextField } from "../../shared/components/inputfields/form-elements";
import { useApi } from "../../shared/hooks/useApi";
import { TCAppConfig } from "../../shared/app-configs/tc-app-configs";
import { useTransactionCertificateStore } from "../../store/transaction-certificates.store";
import { useNavigate } from "react-router-dom";
import useNotification from "../../shared/hooks/useNotification";

interface CBIDData {
  code_1: string;
  code_type_1: string;
  description_1: string;
  status: boolean;
}

const AdminDashboard: React.FC = () => {
  const [cbData, setCbData] = useState<CBIDData[]>([]);
  const [filteredData, setFilteredData] = useState<CBIDData[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const { executeGet } = useApi();
  const { setCbLicensingCode, getUserInfo, cbLicensingCode, originalAdminCBID } = useTransactionCertificateStore();
  const navigate = useNavigate();
  const { showBanner } = useNotification();

  useEffect(() => {
    fetchAllCBIDs();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(cbData);
    } else {
      const filtered = cbData.filter(
        (item) =>
          item.code_1.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [searchQuery, cbData]);

  const fetchAllCBIDs = async () => {
    try {
      setLoading(true);

      const response: any = await executeGet(TCAppConfig.DTRACKIT_BASE_ENDPOINT, {
        query_param: TCAppConfig.GET_CBLICENSING_CODE, // CXF
      });
      
      if (response?.status === 200 && response?.data?.length > 0) {
        // Filter only active CB-IDs (status: true)
        const activeCBIDs = response.data.filter((item: CBIDData) => String(item.status).toLowerCase() === "true");
        setCbData(activeCBIDs);
        setFilteredData(activeCBIDs);
        showBanner(`Successfully loaded ${activeCBIDs.length} CB-IDs`, 'success');
      } else {
        showBanner("No CB-IDs found", 'warning');
      }
    } catch (error) {
      showBanner("Failed to fetch CB-IDs", 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCBIDSelect = (cbid: string) => {
    const isOwnCB = cbid === originalAdminCBID;
    setCbLicensingCode(cbid);
    showBanner(
      isOwnCB
        ? `Switched to your CB-ID: ${cbid} — Edit access enabled`
        : `Switched to CB-ID: ${cbid} — View-only mode`,
      isOwnCB ? 'success' : 'info'
    );
    navigate("/home");
  };

  return (
    <Box sx={{ padding: 4, minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Stack spacing={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold" style={{color: "#070707ff",fontFamily: 'Arial, sans-serif'}}>
            Admin Dashboard - CB-ID Management
          </Typography>
          {cbLicensingCode !== originalAdminCBID && (
            <Box
              sx={{
                px: 2,
                py: 1,
                backgroundColor: "#e3f2fd",
                borderRadius: 1,
                border: "1px solid #2196f3",
              }}
            >
              <Typography variant="body2">
                Currently viewing: <strong>{cbLicensingCode}</strong> (View only) | 
                Your CB-ID: <strong>{originalAdminCBID}</strong> (Edit access)
              </Typography>
            </Box>
          )}
        </Box>

        <Card elevation={3}>
          <CardContent>
            <Stack spacing={3}>
              <CustomTextField
                placeholder="Search CB-ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />

              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="300px"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">CB-ID</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="bold">Access</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData.length > 0 ? (
                        filteredData.map((item, index) => {
                          const isOwnCBID = item.code_1 === originalAdminCBID;
                          return (
                            <TableRow
                              key={`${item.code_1}-${index}`}
                              hover
                              onClick={() => handleCBIDSelect(item.code_1)}
                              sx={{
                                cursor: "pointer",
                                backgroundColor: isOwnCBID ? "#fff3e0" : "inherit",
                                "&:hover": {
                                  backgroundColor: isOwnCBID ? "#ffe0b2" : "#e3f2fd",
                                },
                              }}
                            >
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography fontWeight="500">{item.code_1}</Typography>
                                  {isOwnCBID && (
                                    <Box
                                      component="span"
                                      sx={{
                                        px: 1,
                                        py: 0.25,
                                        backgroundColor: "#ff9800",
                                        color: "white",
                                        borderRadius: 1,
                                        fontSize: "0.7rem",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      YOUR CB-ID
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box
                                  component="span"
                                  sx={{
                                    px: 1,
                                    py: 0.25,
                                    backgroundColor: isOwnCBID ? "#4caf50" : "#9e9e9e",
                                    color: "white",
                                    borderRadius: 1,
                                    fontSize: "0.7rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {isOwnCBID ? "EDIT" : "VIEW ONLY"}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={2} align="center">
                            <Typography color="textSecondary">
                              No CB-IDs found matching your search
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Typography variant="body2" color="textSecondary">
                Total CB-IDs: {filteredData.length} {searchQuery && `(filtered from ${cbData.length})`}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default AdminDashboard;
